import React, { useState } from "react";

import * as StateLib from "@/Utils/StateLib";
import ActionListItem from "../../Composites/ActionListItem/ActionListItem";
import BasicChartSearchMenu from "../../Menus/BasicChartSearchMenu/BasicChartSearchMenu";
import { GetChangeFieldRevertInfo, ValidateRevertField } from "./ConflictResolutionModal.utils";
import {
  isNullish,
  LabelWrapper,
  Radio,
  Select,
  Alert,
  Flex,
  Text
} from "@theorgchartdev/storybook";
import { PlanningChangeDisplayRow } from "@/appComponents/Navigation/RightSidebar/Planning/PlanningChangeDisplayRow";
import cx from "classnames";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { appstr } from "@coreapp/utils/appstr";
import { FormatFieldValue } from "@coreapp/utils/FormatFieldValue";
import {
  enumCellObjectSubtypes,
  enumObjClassNames,
  enumObjectTypes,
  objTypeClassMap
} from "@coreapp/constants";
import { GetObjClassForOverride } from "@coreapp/utils/GetObjClassForOverride";
import { BoxChip } from "@/Utils/BoxChip";

export const ConflictField = props => {
  const [selectedSupervisorLabel, setSelectedSupervisorLabel] = useState("");
  const [showSupervisorSearch, setShowSupervisorSearch] = useState(true);
  const revertInfo = GetChangeFieldRevertInfo(props);

  if (!revertInfo.fieldDef) {
    return null;
  }

  const isPickListField = revertInfo.fieldDef.GetFieldSubtype() === enumCellObjectSubtypes.picklist;
  const isSourceSelected = props.changeFieldReverts[props.field] === true;
  const isPlanSelected = !props.changeFieldReverts[props.field];
  const fieldStatus = ValidateRevertField(props);
  const doc = GetDoc();
  const objClass = GetObjClassForOverride(props.overrideObj, doc);
  const supervisorField = objClass?.GetParentIDFieldDef()?.GetFieldName() ?? "";
  const { mostRecentChange, index } = props.overrideObj.lastActiveChange(true) || {};

  const handlePlanPress = () => {
    props.setIsPickListSelected(true);
    props.setChangeFieldReverts(p => ({
      ...p,
      [props.field]: false
    }));
  };

  const handleSourcePress = () => {
    props.setIsPickListSelected(false);
    props.setChangeFieldReverts(p => ({
      ...p,
      [props.field]: true
    }));
  };

  const handleSelectPickValue = option => {
    const wsMgr = StateLib.GetWSMgr();
    const planMgr = wsMgr?.GetPlanMgr();
    if (isNullish(planMgr)) {
      return;
    }

    props.setIsPickListSelected(true);

    const fieldDef = revertInfo.fieldDef;
    const fieldName = fieldDef.GetFieldName();

    const lastChange = props.overrideObj.lastActiveChange();
    lastChange.fDelta[fieldName] = option.value;

    props.setChangeFieldReverts(p => ({
      ...p,
      [props.field]: false
    }));
  };

  const getFieldLabel = () => {
    let field = props.field;
    if (!revertInfo.recExists) return "";
    if (field === supervisorField) {
      if (doc.IsPositionDocument() && objClass.GetClassName() === enumObjClassNames.box) {
        return appstr("Assigned to");
      }

      return appstr("Reports to");
    }
    return field;
  };

  let planNode = revertInfo.recExists ? (
    <Radio
      id={`${props.field}-plan`}
      data-testid="planning-changes-conflict-field-plan-radio"
      label={isPickListField ? "" : revertInfo.planData}
      onChange={handlePlanPress}
      checked={isPlanSelected}
    />
  ) : mostRecentChange ? (
    <div className={cx("sidebar-flex-column", "sidebar-flex-gap--small")}>
      <PlanningChangeDisplayRow change={mostRecentChange} showCommentBubble changeIndex={index} />
      <Text colorModifier={"lightgrey"} size={"xsmall"}>
        {`${mostRecentChange.GetFormattedChangeDateTime()} by ${mostRecentChange.fChangeAuthor}`}
      </Text>
    </div>
  ) : null;

  let sourceNode = revertInfo.recExists ? (
    <Radio
      id={`${props.field}-source`}
      data-testid="planning-changes-conflict-field-source-radio"
      label={revertInfo.sourceData}
      onChange={handleSourcePress}
      checked={isSourceSelected}
    />
  ) : (
    <Alert severity={"error"} className={cx("oc-padding-small", "oc-text-xsmall")}>
      {appstr("Record removed")}
    </Alert>
  );

  if (!revertInfo.missingRec && !revertInfo.invalidSourceSuper && revertInfo.recExists) {
    const fieldDef = revertInfo.fieldDef;
    const isPickListField = fieldDef.GetFieldSubtype() === enumCellObjectSubtypes.picklist;
    if (isPickListField) {
      const pickValues = fieldDef.GetPickValues();
      const options = pickValues.map(value => ({
        label: FormatFieldValue(value, fieldDef),
        value,
        clickHandler: handleSelectPickValue
      }));
      planNode = (
        <Radio
          id={`${props.field}-plan`}
          data-testid="planning-changes-conflict-field-plan-radio"
          onChange={handlePlanPress}
          checked={isPlanSelected}
          label={
            <Select
              dataTestId="planning-changes-conflict-field-picklist-select"
              value={revertInfo.changeValue}
              inputOnly={false}
              options={options}
              placeholder={revertInfo?.planData}
              status={!fieldStatus?.validPickList ? "error" : "default"}
              message={!fieldStatus?.validPickList ? "Not in pick list" : ""}
              disabled={isSourceSelected}
            />
          }
        />
      );
    }
  }

  if (props.field === supervisorField) {
    const handleSelectSupervisor = opt => {
      const w = GetWSMgr();
      const planMgr = w.GetPlanMgr();
      const objType = opt.result.objType ?? enumObjectTypes.holder;

      const objDb = doc.GetObjDB();
      const parentObjClass = objDb.GetClassByName(objTypeClassMap[objType]);

      const titleFieldDef = parentObjClass.GetTitleFieldDef();
      const idFieldDef = parentObjClass.GetRecordIDFieldDef();

      const boxChip = BoxChip.FromSearchResult(opt.result, opt.isRecent, doc);
      const label = boxChip.label();
      const id = boxChip.getValue();

      setSelectedSupervisorLabel(label);
      setShowSupervisorSearch(false);
      handleSelectPickValue(opt);

      const payload = {};
      if (objType === enumObjectTypes.position) {
        payload.positionIDs = [id];
      } else {
        payload.recordIDs = [id];
      }

      planMgr.LoadRecords(payload, () => {
        const cachedObj = planMgr.GetCacheBaseRecord(objType, id);
        props.setPlanSupervisor(cachedObj);
      });
    };

    if (showSupervisorSearch) {
      planNode = (
        <Flex direction={"column"} gap={"8px"}>
          {revertInfo.invalidPlanSuper && (
            <Alert severity={"error"} className={cx("oc-padding-small", "oc-text-xsmall")}>
              {appstr("Reports to removed")}
            </Alert>
          )}
          <Radio
            id={`${props.field}-plan`}
            data-testid="planning-changes-conflict-field-plan-radio"
            onChange={handlePlanPress}
            checked={isPlanSelected}
            label={
              <BasicChartSearchMenu
                dataTestId="planning-changes-conflict-supervisor-search-input"
                optionSize={"small"}
                inputSize={"small"}
                listWidth={"fill"}
                searchMenuProps={{ clearOnSelect: true, setPlaceholderOnSelect: true }}
                handleSearchOptionClick={handleSelectSupervisor}
                placeholder={selectedSupervisorLabel}
                hideResultId={props.overrideObj.SmartGetFieldValue("RefID")}
              />
            }
          />
        </Flex>
      );
    } else {
      planNode = (
        <Flex direction={"column"} gap={"8px"}>
          {revertInfo.invalidPlanSuper && (
            <Alert severity={"error"} className={cx("oc-padding-small", "oc-text-xsmall")}>
              {appstr("Reports to removed")}
            </Alert>
          )}
          <Radio
            id={`${props.field}-plan`}
            data-testid="planning-changes-conflict-field-plan-radio"
            onChange={handlePlanPress}
            checked={isPlanSelected}
            label={
              <ActionListItem
                padding={"0"}
                labelProps={{ size: "small", modifier: "bold" }}
                label={selectedSupervisorLabel}
                visible="always"
                actions={[
                  {
                    iconName: "pen",
                    dataTestId: "planning-changes-conflict-supervisor-edit-btn",
                    onClick: () => {
                      setShowSupervisorSearch(true);
                    }
                  }
                ]}
              />
            }
          />
        </Flex>
      );
    }
  }

  return (
    <div className="oc-conflict-resolution-row">
      <LabelWrapper label={revertInfo.recExists ? getFieldLabel() : ""}>
        <div className="oc-conflict-resolution-row-content">
          {planNode}
          {sourceNode}
        </div>
      </LabelWrapper>
    </div>
  );
};
